// src/utils/grossSalesTransactionCsvUtils.js

/**
 * Convert an array of objects into a CSV string.
 * Uses the keys of the first object as headers.
 * @param {Object[]} objArray
 * @returns {string}
 */
export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  // Header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // Data rows
  for (const row of objArray) {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  }

  return csv;
}

/**
 * Generate and download a CSV for Gross Sales vs Total Transactions per month.
 *
 * @param {Object} params
 * @param {{ labels: string[], datasets: Array<{ label: string, data: number[] }> }} params.chartData
 * @param {string} params.storess                - Store name (e.g. "All Stores" or specific)
 * @param {string} params.selectedMonth          - JSON stringified month range ({ $gte, $lte }) or "AllMonths"
 * @param {string} params.selectedYear           - Year (e.g. "2025")
 * @param {string} [params.fileName]             - Filename for download
 */
export function exportGrossSalesTransactionsCSV({
  chartData,
  storess,
  selectedMonth,
  selectedYear,
  fileName = "GrossSales-TotalTransactions.csv",
}) {
  if (!chartData || !chartData.labels || !chartData.datasets) {
    console.error("exportGrossSalesTransactionsCSV: invalid chartData");
    return;
  }

  const totalTxDataset = chartData.datasets.find(
    (ds) => ds.label === "Total Transactions"
  );
  const growthSalesDataset = chartData.datasets.find(
    (ds) => ds.label === "Growth Sales"
  );
  if (!totalTxDataset || !growthSalesDataset) {
    console.error("exportGrossSalesTransactionsCSV: missing datasets");
    return;
  }

  // Determine month label
  let monthLabel = "All Months";
  if (selectedMonth && selectedMonth !== "AllMonths") {
    try {
      const range = JSON.parse(selectedMonth);
      const from = range.$lte?.split("T")[0] || "";
      const to = range.$gte?.split("T")[0] || "";
      monthLabel = `${selectedYear}-${from} ${selectedYear}-${to}`;
    } catch (e) {
      console.warn(
        "exportGrossSalesTransactionsCSV: could not parse selectedMonth",
        e
      );
    }
  }

  // Build CSV data
  const rows = chartData.labels.map((label, i) => ({
    Brand: "Angels Pizza",
    Store: storess || "All Stores",
    Month: monthLabel,
    TotalTransactions: totalTxDataset.data[i],
    GrowthSales: growthSalesDataset.data[i],
  }));

  const csvString = convertToCSV(rows);
  if (!csvString) {
    console.warn("exportGrossSalesTransactionsCSV: no data to export");
    return;
  }

  // Download
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
